/**
 * Error Boundary Component
 * Catches React errors and displays a fallback UI
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            errorInfo: null,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        
        this.setState({
            error,
            errorInfo,
        });
        
        // Call optional error handler
        this.props.onError?.(error, errorInfo);
        
        // Send to error tracking service (e.g., Sentry)
        // Sentry.captureException(error, { extra: errorInfo });
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default error UI
            return (
                <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-red-900 flex items-center justify-center p-4">
                    <div className="max-w-2xl w-full">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8">
                            {/* Icon */}
                            <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <AlertTriangle className="w-12 h-12 text-red-600 dark:text-red-400" />
                            </div>
                            
                            {/* Title */}
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-4">
                                Oops! Something went wrong
                            </h1>
                            
                            {/* Description */}
                            <p className="text-gray-600 dark:text-gray-400 text-center mb-8">
                                We're sorry for the inconvenience. The application encountered an unexpected error.
                            </p>
                            
                            {/* Error Details (Development only) */}
                            {process.env.NODE_ENV === 'development' && this.state.error && (
                                <div className="mb-8 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                    <p className="text-sm font-mono text-red-800 dark:text-red-300 mb-2">
                                        <strong>Error:</strong> {this.state.error.message}
                                    </p>
                                    {this.state.errorInfo && (
                                        <details className="mt-3">
                                            <summary className="text-sm font-medium text-red-700 dark:text-red-400 cursor-pointer hover:underline">
                                                Stack Trace
                                            </summary>
                                            <pre className="mt-2 text-xs text-red-600 dark:text-red-400 overflow-x-auto p-3 bg-red-100 dark:bg-red-900/30 rounded">
                                                {this.state.errorInfo.componentStack}
                                            </pre>
                                        </details>
                                    )}
                                </div>
                            )}
                            
                            {/* Actions */}
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    onClick={this.handleReset}
                                    className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                                >
                                    <RefreshCw className="w-5 h-5" />
                                    Try Again
                                </button>
                                
                                <button
                                    onClick={() => window.location.href = '/'}
                                    className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
                                >
                                    <Home className="w-5 h-5" />
                                    Go Home
                                </button>
                            </div>
                            
                            {/* Support Info */}
                            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    If this problem persists, please contact support with the error details above.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Functional wrapper for hooks support
export function ErrorBoundaryWrapper({ children }: { children: ReactNode }) {
    return (
        <ErrorBoundary
            onError={(error, errorInfo) => {
                // Log to console in development
                if (process.env.NODE_ENV === 'development') {
                    console.error('Error caught by boundary:', error, errorInfo);
                }
                
                // TODO: Send to error tracking service
                // logErrorToService(error, errorInfo);
            }}
        >
            {children}
        </ErrorBoundary>
    );
}
